import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-hot-toast";
import FacebookIcon from "@mui/icons-material/Facebook";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const apiUrl = `${process.env.REACT_APP_API}`;
const MAX_CHARS = 2000;

const errorMessage = (err, fallback) =>
  (err && err.response && err.response.data && err.response.data.error) || fallback;

/**
 * Lets the admin review and edit a Facebook post for one product, then
 * publish it to the shop's Page. Nothing is sent until "Post" is clicked.
 *
 * Props: product ({ _id, productName }), show, onClose, onExited, onPosted(productId, facebookInfo)
 */
const FacebookPostModal = ({ product, show, onClose, onExited, onPosted }) => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [mediaType, setMediaType] = useState("photos"); // "photos" | "video"
  const [suggesting, setSuggesting] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const productId = product && product._id;

  useEffect(() => {
    if (!show || !productId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setPreview(null);

    axios
      .get(`${apiUrl}/facebook/preview/${productId}`)
      .then(({ data }) => {
        if (cancelled) return;
        setPreview(data);
        setMessage(data.message || "");
        setSelectedKeys((data.images || []).map((img) => img.key));
        // Video posts reach more people, so lead with the video when there is one.
        setMediaType(data.video ? "video" : "photos");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(errorMessage(err, "Could not prepare the post. Please try again."));
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [show, productId]);

  const toggleImage = (key) => {
    setSelectedKeys((keys) =>
      keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
    );
  };

  const suggestCaption = async () => {
    setSuggesting(true);
    try {
      const { data } = await axios.post(`${apiUrl}/facebook/caption/${productId}`);
      setMessage(data.message || message);
      if (data.source === "ai") {
        toast.success("Here's a fresh caption — edit it however you like.");
      } else {
        toast("AI was unavailable, so this is the standard caption.", { icon: "ℹ️" });
      }
    } catch (err) {
      toast.error(errorMessage(err, "Could not write a caption right now."));
    } finally {
      setSuggesting(false);
    }
  };

  const publish = async () => {
    if (!message.trim()) {
      toast.error("The post needs some text.");
      return;
    }
    if (mediaType === "photos" && selectedKeys.length === 0) {
      toast.error("Pick at least one photo for the post.");
      return;
    }
    setPosting(true);
    setError("");
    try {
      const { data } = await axios.post(`${apiUrl}/facebook/post/${productId}`, {
        message,
        imageKeys: selectedKeys,
        mediaType,
      });
      toast.success(
        (t) => (
          <span>
            {mediaType === "video"
              ? "Posted! Facebook is processing the video — it appears on the Page within a few minutes. "
              : "Posted to Facebook! "}
            {data.permalink && (
              <a href={data.permalink} target="_blank" rel="noopener noreferrer">
                View post
              </a>
            )}
          </span>
        ),
        { duration: 8000 }
      );
      if (data.firstPost) {
        toast(
          "Check the post from a phone or private window that isn't logged in as the Page admin. If you can't see it there, the Facebook app is still in Development mode — see the setup guide.",
          { icon: "👀", duration: 15000 }
        );
      }
      if (onPosted) onPosted(productId, data);
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Facebook did not accept the post. Please try again."));
    } finally {
      setPosting(false);
    }
  };

  const images = (preview && preview.images) || [];
  const warnings = (preview && preview.warnings) || [];
  const posted = preview && preview.posted;
  const notConnected = preview && preview.configured === false;
  const tooLong = message.length > MAX_CHARS;

  return (
    <Modal show={show} onHide={posting ? undefined : onClose} onExited={onExited} size="lg" centered>
      <Modal.Header closeButton={!posting}>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FacebookIcon style={{ color: "#1877F2" }} />
          Post to Facebook
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 mb-0 text-muted">Preparing the post…</p>
          </div>
        )}

        {!loading && error && !preview && <div className="alert alert-danger mb-0">{error}</div>}

        {!loading && preview && (
          <>
            {notConnected && (
              <div className="alert alert-warning">
                Facebook isn't connected yet. Follow <code>docs/FACEBOOK_SETUP.md</code> to
                connect the Page, then come back here.
              </div>
            )}

            {posted && (
              <div className="alert alert-info d-flex justify-content-between align-items-center">
                <span>
                  This product was already posted
                  {posted.postedAt && ` on ${new Date(posted.postedAt).toLocaleDateString()}`}.
                  Posting again creates a second post.
                </span>
                {posted.permalink && (
                  <a
                    className="btn btn-sm btn-outline-primary ms-3 text-nowrap"
                    href={posted.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View <OpenInNewIcon fontSize="inherit" />
                  </a>
                )}
              </div>
            )}

            {warnings.map((w) => (
              <div key={w} className="alert alert-warning py-2">
                {w}
              </div>
            ))}

            <div className="row g-4">
              <div className="col-md-5">
                {preview.video && (
                  <div className="btn-group w-100 mb-3" role="group" aria-label="Post as">
                    {[
                      ["video", "🎬 Video"],
                      ["photos", "🖼️ Photos"],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={`btn btn-sm ${mediaType === value ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setMediaType(value)}
                        disabled={posting}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                {mediaType === "video" && preview.video && (
                  <>
                    <label className="form-label fw-bold">Video</label>
                    <video
                      src={preview.video}
                      controls
                      muted
                      playsInline
                      className="w-100 rounded border bg-dark"
                      style={{ maxHeight: "340px" }}
                    />
                    <p className="text-muted small mt-2 mb-0">
                      Posted as a video post (photos aren't attached to it). Facebook finishes
                      processing the video after posting, so it appears within a few minutes.
                    </p>
                  </>
                )}

                {mediaType === "photos" && (
                  <>
                <label className="form-label fw-bold">
                  Photos ({selectedKeys.length} of {images.length} selected)
                </label>
                {images.length === 0 && (
                  <p className="text-danger">This product has no images to post.</p>
                )}
                <div className="row g-2">
                  {images.map((img) => {
                    const checked = selectedKeys.includes(img.key);
                    return (
                      <div key={img.key} className="col-6">
                        <label
                          className="d-block position-relative"
                          style={{ cursor: "pointer" }}
                          title={`${checked ? "Remove" : "Include"} ${img.key} photo`}
                        >
                          <img
                            src={img.url}
                            alt={`${product.productName} – ${img.key}`}
                            className="w-100 rounded border"
                            style={{
                              height: "140px",
                              objectFit: "cover",
                              opacity: checked ? 1 : 0.4,
                              outline: checked ? "3px solid #1877F2" : "none",
                            }}
                          />
                          <input
                            type="checkbox"
                            className="form-check-input position-absolute"
                            style={{ top: "8px", left: "8px" }}
                            checked={checked}
                            onChange={() => toggleImage(img.key)}
                          />
                          <span
                            className="badge bg-dark position-absolute text-capitalize"
                            style={{ bottom: "8px", left: "8px" }}
                          >
                            {img.key}
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>
                <p className="text-muted small mt-2 mb-0">
                  The first selected photo shows largest. Facebook fetches them from the website.
                </p>
                  </>
                )}
              </div>

              <div className="col-md-7">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label htmlFor="fb-message" className="form-label fw-bold mb-0">
                    Post text
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={suggestCaption}
                    disabled={suggesting || posting}
                  >
                    {suggesting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" /> Writing…
                      </>
                    ) : (
                      <>
                        <AutoAwesomeIcon fontSize="small" /> Suggest with AI
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  id="fb-message"
                  className={`form-control ${tooLong ? "is-invalid" : ""}`}
                  rows={14}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={posting}
                />
                <div className="d-flex justify-content-between mt-1">
                  <small className="text-muted">
                    Tip: the first line is what people see before "See more".
                  </small>
                  <small className={tooLong ? "text-danger" : "text-muted"}>
                    {message.length} / {MAX_CHARS}
                  </small>
                </div>
              </div>
            </div>

            {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={posting}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-1"
          style={{ backgroundColor: "#1877F2", borderColor: "#1877F2" }}
          onClick={publish}
          disabled={
            loading ||
            posting ||
            !preview ||
            notConnected ||
            tooLong ||
            (mediaType === "photos" && selectedKeys.length === 0)
          }
        >
          {posting ? (
            <>
              <span className="spinner-border spinner-border-sm" /> Posting…
            </>
          ) : (
            <>
              <FacebookIcon fontSize="small" />{" "}
              {posted ? "Post again" : mediaType === "video" ? "Post video to Facebook" : "Post to Facebook"}
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default FacebookPostModal;
